import { ImgWithFallback } from "@/components/img-with-fallback";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Building2, ArrowRight, Sparkles, Plus, Users, Package, Crosshair, Layers } from "lucide-react";
import { Link } from "wouter";
import type { Brand, Workspace } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";
import { optimizedImg } from "@/lib/img";
import { useAuth } from "@/hooks/use-auth";
import { groupBrandsByClientCategory } from "@/lib/brand-client-categories";

function MetricPill({ label, value, icon: Icon }: { label: string; value: number; icon: any }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border/50" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="w-8 h-8 rounded-lg bg-primary/8 flex items-center justify-center">
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div>
        <p className="text-xl font-bold tracking-tight leading-none">{value}</p>
        <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mt-0.5">{label}</p>
      </div>
    </div>
  );
}

function BrandCard({ brand }: { brand: Brand }) {
  return (
    <Link href={`/brands/${brand.id}`}>
      <Card className="cursor-pointer h-full group relative overflow-hidden border-border/50 hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300" data-testid={`card-brand-${brand.id}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <CardContent className="relative p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 group-hover:scale-105 transition-all duration-300 overflow-hidden">
              <ImgWithFallback
                src={brand.logoUrl ? optimizedImg(brand.logoUrl, 80, 70) : undefined}
                alt={`${brand.name} logo`}
                loading="lazy"
                className="w-full h-full object-cover rounded-xl"
                data-testid={`img-brand-logo-${brand.id}`}
                fallback={<Building2 className="h-5 w-5 text-primary" />}
              />
            </div>
            <Badge
              variant={brand.status === "active" ? "default" : "secondary"}
              className="text-[10px] rounded-full px-2.5 shrink-0"
            >
              {brand.status}
            </Badge>
          </div>
          <h3 className="font-semibold tracking-tight mb-1" data-testid={`text-brand-name-${brand.id}`}>{brand.name}</h3>
          {brand.category && (
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium mb-3">{brand.category}</p>
          )}
          <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed mb-4">
            {brand.summary || "No description yet"}
          </p>
          <div className="flex items-center gap-1.5 text-xs text-primary font-medium opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
            Open workspace <ArrowRight className="h-3 w-3" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const canCreateBrand = !!user?.isMaster || !!user?.canCreateBrands;
  const { data: workspaces, isLoading: wsLoading } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const workspaceId = workspaces?.[0]?.id;

  const { data: brands, isLoading: brandsLoading } = useQuery<Brand[]>({
    queryKey: [`/api/brands?workspaceId=${workspaceId}`],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!workspaceId,
  });

  const isLoading = wsLoading || brandsLoading;
  const activeBrands = brands?.filter(b => b.status === "active").length || 0;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-10">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/10 px-8 py-10 fade-in-up">
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="relative">
          <Badge variant="secondary" className="rounded-full text-[10px] font-medium uppercase tracking-wider px-3 py-1 mb-4 bg-primary/10 text-primary border-0">
            {workspaces?.[0]?.name || "Workspace"}
          </Badge>
          <h1 className="text-3xl font-bold tracking-tight mb-2" data-testid="text-dashboard-title">
            Creative Command Center
          </h1>
          <p className="text-sm text-muted-foreground max-w-lg leading-relaxed">
            Manage your brand strategy, personas, and campaign creative from one place. Select a brand below to open its workspace.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-48 rounded-xl" />)}
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 fade-in-up-delay-1">
            <MetricPill label="Total Brands" value={brands?.length || 0} icon={Building2} />
            <MetricPill label="Active" value={activeBrands} icon={Layers} />
            <MetricPill label="Categories" value={new Set(brands?.map(b => b.category).filter(Boolean)).size || 0} icon={Package} />
            <MetricPill label="Workspaces" value={workspaces?.length || 0} icon={Crosshair} />
          </div>

          <div className="fade-in-up-delay-2">
            <div className="flex items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Your Brands</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Select a brand to open its creative workspace</p>
              </div>
              <Link href="/brands">
                <Button variant="ghost" size="sm" className="text-muted-foreground text-xs" data-testid="link-view-all-brands">
                  View all <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
            {brands && brands.length > 0 ? (
              user?.isMaster ? (
                <div className="space-y-8">
                  {groupBrandsByClientCategory(brands).map((group) => (
                    <section key={group.key} data-testid={`dashboard-brand-group-${group.key}`}>
                      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {group.label} ({group.items.length})
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {group.items.map((brand) => (
                          <BrandCard key={brand.id} brand={brand} />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {brands.map((brand) => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              )
            ) : (
              <Card className="border-dashed border-border/50">
                <CardContent className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                    <Sparkles className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold tracking-tight text-lg mb-1.5">
                    {canCreateBrand ? "Create your first brand" : "No brand access yet"}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-8 max-w-sm leading-relaxed">
                    {canCreateBrand
                      ? "Start by adding a brand to your workspace. Define its doctrine, personas, and creative strategy."
                      : "You don't have access to any brands yet. Ask an admin to grant you access to a brand."}
                  </p>
                  {canCreateBrand && (
                    <Link href="/brands">
                      <Button className="rounded-full px-8 h-10" data-testid="button-create-first-brand">
                        <Plus className="h-4 w-4 mr-2" /> Create Brand
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}
    </div>
  );
}
